import React, { Suspense, useCallback, useRef } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { PlatformSiteAri } from '@atlassian/ari';
import { LoadingSkeleton } from '@atlassian/user-profile-preview/loading';
import {
	UserProfilePreviewRelay,
	type UserProfilePreviewRelayProps,
} from '@atlassian/user-profile-preview/relay';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import { onKnowledgeCardPartialDegradation } from '../on-knowledge-card-partial-degradation';
import { onPersonKnowledgeCardLoaded } from '../on-person-knowledge-card-loaded';
import type { singlePersonKnowledgeCardRelayQuery } from './__generated__/singlePersonKnowledgeCardRelayQuery.graphql';
import { SinglePersonKnowledgeCardRelay } from './single-person-knowledge-card-relay';

export type SinglePersonKnowledgeCardProps = {
	accountId: string;
	children?: React.ReactNode;
	onError?: UserProfilePreviewRelayProps['onError'];
	cloudId: string;
	orgId: string;
	requestingUserId: string;
	sourceProduct?: string;
	onLinkClick?: (
		url: string,
		index: number,
		opts?: { isAuxClick?: boolean; isContextMenu?: boolean },
	) => void;
};

function buildSiteAri(cloudId: string, sourceProduct?: string): string {
	if (sourceProduct) {
		return `ari:cloud:${sourceProduct}::site/${cloudId}`;
	}
	return cloudId;
}

/**
 * Builds the collaboration-context ARI used to scope the recent-work (`workedOn`)
 * query to the origin site, so activity from other sites the person belongs to is
 * not returned. Returns `null` when the site ID is missing or invalid so the query
 * safely falls back to unscoped behaviour.
 */
function buildCollabContextId(cloudId: string): string | null {
	if (!cloudId) {
		return null;
	}
	try {
		return PlatformSiteAri.create({ siteId: cloudId }).toString();
	} catch {
		return null;
	}
}

/**
 * Person knowledge card for a single selected user: loads profile, recent work, collaborators,
 * and reporting lines via one Relay query and the local `SinglePersonKnowledgeCardRelay` (`single-person-knowledge-card-relay`).
 * Must render under a Relay environment and a React `Suspense` boundary.
 */
function LegacySinglePersonKnowledgeCard(props: SinglePersonKnowledgeCardProps): React.JSX.Element {
	const { accountId, cloudId, orgId, requestingUserId, sourceProduct, onLinkClick } = props;
	const accountIdARI = `ari:cloud:identity::user/${accountId}`;
	const featureServiceModel = SAINFeatureControl.getCollabGraphExperimentModel();

	const data = useLazyLoadQuery<singlePersonKnowledgeCardRelayQuery>(
		graphql`
			query singlePersonKnowledgeCardRelayQuery(
				$first: Int!
				$filter: ActivityFilter
				$recommendationsQuery: SmartsRecommendationsQuery!
				$reportingLinesAaid: String!
				$reportingLinesOrgId: String!
				$reportingLinesSiteId: String!
				$singleUserAccountId: String!
				$singleUserRequestingUserId: String!
				$singleUserTenantId: String!
			) {
				activity @catch(to: RESULT) {
					workedOn(first: $first, filter: $filter) {
						...singlePersonKnowledgeCardRelay_workedOn
					}
				}
				smarts @catch(to: RESULT) {
					...singlePersonKnowledgeCardRelay_smarts
						@arguments(recommendationsQuery: $recommendationsQuery)
					recommendedUser(recommendationsQuery: $recommendationsQuery) @catch(to: RESULT) {
						__typename
					}
				}
				knowledgeDiscovery {
					...singlePersonKnowledgeCardRelay_user
						@arguments(
							accountId: $singleUserAccountId
							requestingUserId: $singleUserRequestingUserId
							tenantId: $singleUserTenantId
						)
					...singlePersonKnowledgeCardRelay_reportingLines
						@arguments(
							aaid: $reportingLinesAaid
							orgId: $reportingLinesOrgId
							siteId: $reportingLinesSiteId
						)
					reportingLines(
						aaid: $reportingLinesAaid
						orgId: $reportingLinesOrgId
						siteId: $reportingLinesSiteId
					) @catch(to: RESULT) @optIn(to: "KnowledgeDiscovery Get reporting lines") {
						__typename
						... on QueryError {
							__typename
						}
					}
				}
			}
		`,
		{
			first: 50,
			singleUserAccountId: accountId,
			singleUserRequestingUserId: requestingUserId,
			singleUserTenantId: cloudId,
			filter: { actors: [accountIdARI], collabContextId: buildCollabContextId(cloudId) },
			recommendationsQuery: {
				context: {
					userId: accountId,
					tenantId: cloudId,
				},
				modelRequestParams: {
					caller: 'search-ai',
					experience: 'CgUserNearbyUser',
					featureServiceModel,
				},
				requestingUserId,
				maxNumberOfResults: 10,
			},
			reportingLinesAaid: accountId,
			reportingLinesOrgId: orgId,
			reportingLinesSiteId: buildSiteAri(cloudId, sourceProduct),
		},
	);
	const reportingLines = data.knowledgeDiscovery?.reportingLines;
	return (
		<SinglePersonKnowledgeCardRelay
			userFragment={data.knowledgeDiscovery ?? undefined}
			workedOnFragment={
				data.activity?.ok ? (data.activity.value?.workedOn ?? undefined) : undefined
			}
			smartsFragment={data.smarts?.ok ? (data.smarts.value ?? undefined) : undefined}
			reportingLinesFragment={data.knowledgeDiscovery ?? undefined}
			collaboratorsFailed={
				data.smarts?.ok === false ||
				(data.smarts?.ok === true && data.smarts.value?.recommendedUser.ok === false)
			}
			recentWorkFailed={data.activity?.ok === false}
			reportingLinesFailed={
				reportingLines?.ok === false ||
				(reportingLines?.ok === true && reportingLines.value?.__typename === 'QueryError')
			}
			cloudId={cloudId}
			orgId={orgId}
			siteId={buildSiteAri(cloudId, sourceProduct)}
			requestingUserId={requestingUserId}
			testId="individual-person-knowledge-card"
			onLinkClick={onLinkClick}
		/>
	);
}

function SharedSinglePersonKnowledgeCard(props: SinglePersonKnowledgeCardProps): React.JSX.Element {
	const { commonAttributes } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const hasFiredLoaded = useRef(false);
	const hasFiredDegradation = useRef(false);
	const onLoad = useCallback<NonNullable<UserProfilePreviewRelayProps['onLoad']>>(
		({ collaboratorsFailed, recentWorkFailed, reportingLinesFailed }) => {
			if (!hasFiredLoaded.current) {
				hasFiredLoaded.current = true;
				fireAnalyticsEvent(
					onPersonKnowledgeCardLoaded({ ...commonAttributes, personCardVariant: 'singlePerson' }),
				);
			}
			if (
				!SAINFeatureControl.isPeopleConnectorUXEnabled() ||
				hasFiredDegradation.current ||
				(!collaboratorsFailed && !recentWorkFailed && !reportingLinesFailed)
			) {
				return;
			}
			hasFiredDegradation.current = true;
			fireAnalyticsEvent(
				onKnowledgeCardPartialDegradation({
					...commonAttributes,
					personCardVariant: 'singlePerson',
					failedDatasets: [
						...(collaboratorsFailed ? (['collaborators'] as const) : []),
						...(recentWorkFailed ? (['recentWork'] as const) : []),
						...(reportingLinesFailed ? (['reportingLines'] as const) : []),
					],
				}),
			);
		},
		[commonAttributes, fireAnalyticsEvent],
	);
	return (
		<UserProfilePreviewRelay
			{...props}
			caller="search-ai"
			featureServiceModel={SAINFeatureControl.getCollabGraphExperimentModel()}
			testId="individual-person-knowledge-card"
			onLoad={onLoad}
		/>
	);
}

export function SinglePersonKnowledgeCard(
	props: SinglePersonKnowledgeCardProps,
): React.JSX.Element {
	return fg('kd_shared_inline_person_card') ? (
		<SharedSinglePersonKnowledgeCard key={props.accountId} {...props} />
	) : (
		<Suspense fallback={<LoadingSkeleton />}>
			<LegacySinglePersonKnowledgeCard {...props} />
			{props.children}
		</Suspense>
	);
}
